import {
  Avatar,
  Button,
  Card,
  CardBody,
  CardHeader,
  CardFooter,
  Divider,
} from '@heroui/react';
import _ from 'lodash';
import dayjs from 'dayjs';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { Icon } from '@iconify/react';

import useSurveyDrafts from '@/hooks/useSurveyDrafts';
import { AppEvent } from '@/patient-app-common/types/ApplicationEvent';

interface SurveyEventProps {
  avatar: string | undefined;
  disableRead?: boolean;
  event: AppEvent;
}

export function SurveyEvent({ avatar, event }: SurveyEventProps) {
  const { t } = useTranslation('home');
  const navigate = useNavigate();
  const { surveys, surveyDrafts } = useSurveyDrafts();

  const surveyId = _.toNumber(event.options.id);

  const hasDraft = useMemo(() => {
    if (!surveyDrafts?.length) return false;
    return _.find(surveyDrafts, {
      applicationEventId: event.id,
      procedureId: surveyId,
    });
    return false;
  }, [event, surveyId, surveyDrafts]);

  let btnText = event.options.linkText ?? t('common.button.survey_start');
  btnText = hasDraft ? t('common.button.survey_edit') : btnText;

  const submittedDate = useMemo(() => {
    if (!event.shouldPersistAfterReading) return '';

    const lastEntryDate = _.find(surveys, ['id', surveyId])?.lastEntryDate;

    if (lastEntryDate) {
      return dayjs(lastEntryDate).fromNow();
    }

    return '';
  }, [event, surveyId, surveys]);

  return (
    <article>
      <Card radius="sm" shadow="sm">
        <p className="font-light p-2 text-sm">
          {dayjs(event.startDateTime).format('LL')}
        </p>
        <CardHeader>
          <Avatar
            radius="full"
            size="md"
            src={avatar}
            classNames={{ base: 'bg-avatarBg' }}
          />
          <h2 className="font-semibold px-4">{event.title}</h2>
        </CardHeader>
        <Divider />
        <CardBody>
          <p className="px-8 whitespace-pre-line">{event.body}</p>
        </CardBody>
        <CardFooter className="justify-end gap-2">
          <Button
            color="primary"
            onPress={() =>
              navigate(`/tracker/${event.options.id}`, {
                state: { eventId: event.id },
              })
            }
          >
            {btnText}
          </Button>
        </CardFooter>
        {!_.isEmpty(submittedDate) && (
          <div className="bg-primary-100 p-1">
            <p className="font-light text-sm">
              {t('common.text.last_completed_date', { date: submittedDate })}
            </p>
          </div>
        )}
        {hasDraft && (
          <div className="bg-primary-100 flex items-center p-1">
            <Icon icon="mdi:pencil" className="mr-1" />
            <p className="font-light text-sm">{t('common.text.has_draft')}</p>
          </div>
        )}
      </Card>
    </article>
  );
}
